import React, { useEffect, useMemo, useState } from 'react';
import type { UserManagerSettings } from 'oidc-client-ts';
import { FrontendAuthProvider, type FrontendAuthIdentity, type FrontendAuthSession } from './session.js';
import { registerFrontendAccessTokenAccessor } from './token-accessor.js';
import { createMockFrontendAuthSession, resolveFrontendDevProfile } from './mock-session.js';
import { useOidcAuthSession } from './oidc-session.js';
import { type WorkspaceRuntimeMode } from './runtime.js';
import { buildFrontendIdentityFromOidcUser } from './oidc-identity.js';
import {
  EMBEDDED_AUTH_REQUEST,
  EMBEDDED_AUTH_REQUEST_TIMEOUT_MS,
  EMBEDDED_AUTH_RESPONSE,
  readEmbeddedShellOrigin,
  type EmbeddedAuthPayload,
  type EmbeddedAuthResponseMessage,
} from './embedded-auth-bridge.js';
import { AUTHENTICATION_REQUIRED_EVENT, type AuthenticationRequiredMessage } from './authentication-required.js';

export interface WorkspaceAuthProviderProps {
  readonly runtime: WorkspaceRuntimeMode;
  readonly search: string;
  readonly authMode?: 'oidc' | 'mock';
  readonly oidcSettings?: UserManagerSettings;
  readonly oidcClientId?: string;
  readonly defaultProfileCode?: string;
  readonly envProfile?: string;
  readonly profileStorageKey?: string;
  readonly children: React.ReactNode;
}

function resolveEmbeddedAuthMode(search: string, fallback: 'oidc' | 'mock' = 'oidc'): 'oidc' | 'mock' {
  const queryMode = new URLSearchParams(search).get('authMode');
  if (queryMode === 'oidc' || queryMode === 'mock') {
    return queryMode;
  }

  return fallback;
}

function createEmbeddedSession(runtime: WorkspaceRuntimeMode): FrontendAuthSession {
  return {
    mode: 'embedded',
    runtime,
    identity: null,
    isAuthenticated: true,
    isLoading: false,
    error: null,
    accessToken: null,
    login: async () => undefined,
    logout: async () => undefined,
    handleCallback: async () => null,
  };
}

function createEmbeddedLoadingSession(runtime: WorkspaceRuntimeMode): FrontendAuthSession {
  return {
    ...createEmbeddedSession(runtime),
    isLoading: true,
    isAuthenticated: false,
  };
}

function createDisabledSession(runtime: WorkspaceRuntimeMode): FrontendAuthSession {
  return {
    mode: 'oidc',
    runtime,
    identity: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
    accessToken: null,
    login: async () => undefined,
    logout: async () => undefined,
    handleCallback: async () => null,
  };
}

function useEmbeddedWorkspaceAuthSession(runtime: WorkspaceRuntimeMode, search: string): FrontendAuthSession {
  const [payload, setPayload] = useState<EmbeddedAuthPayload | null>(null);
  const [isLoading, setIsLoading] = useState(runtime === 'embedded');

  useEffect(() => {
    if (runtime !== 'embedded' || typeof window === 'undefined') {
      setPayload(null);
      setIsLoading(false);
      return;
    }

    const shellOrigin = readEmbeddedShellOrigin(search);

    if (!shellOrigin || window.parent === window) {
      setPayload(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let responded = false;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== shellOrigin || event.source !== window.parent) {
        return;
      }

      const data = event.data as EmbeddedAuthResponseMessage | undefined;
      if (!data || data.type !== EMBEDDED_AUTH_RESPONSE) {
        return;
      }

      responded = true;
      setPayload(data.payload);
      setIsLoading(false);
      window.clearTimeout(timeoutId);
    };

    window.addEventListener('message', handleMessage);
    window.parent.postMessage({ type: EMBEDDED_AUTH_REQUEST }, shellOrigin);

    const timeoutId = window.setTimeout(() => {
      if (responded) {
        return;
      }
      setPayload(null);
      setIsLoading(false);
    }, EMBEDDED_AUTH_REQUEST_TIMEOUT_MS);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.clearTimeout(timeoutId);
    };
  }, [runtime, search]);

  return useMemo(() => {
    if (runtime !== 'embedded') {
      return createDisabledSession(runtime);
    }

    if (isLoading) {
      return createEmbeddedLoadingSession(runtime);
    }

    return {
      ...createEmbeddedSession(runtime),
      identity: (payload?.identity as FrontendAuthIdentity | null | undefined) ?? null,
      isAuthenticated: payload?.isAuthenticated ?? false,
      accessToken: payload?.accessToken ?? null,
    };
  }, [isLoading, payload, runtime]);
}

function useWorkspaceAuthSession(props: WorkspaceAuthProviderProps): FrontendAuthSession {
  const oidc = useOidcAuthSession({
    settings: props.oidcSettings ?? {
      authority: 'http://localhost/invalid-authority',
      client_id: 'invalid-client',
      redirect_uri: 'http://localhost/auth/callback',
      post_logout_redirect_uri: 'http://localhost',
      response_type: 'code',
      scope: 'openid profile email',
    },
  });

  const embeddedSession = useEmbeddedWorkspaceAuthSession(props.runtime, props.search);
  const embeddedAuthMode = useMemo(
    () => resolveEmbeddedAuthMode(props.search, props.authMode ?? 'oidc'),
    [props.authMode, props.search],
  );
  const mockProfileCode = useMemo(() => {
    const mockEnabled = props.runtime === 'embedded' ? embeddedAuthMode === 'mock' : props.authMode === 'mock';
    if (!mockEnabled) return null;
    return resolveFrontendDevProfile({
      search: props.search,
      envProfile: props.envProfile,
      defaultProfileCode: props.defaultProfileCode ?? '',
      storageKey: props.profileStorageKey,
    });
  }, [
    embeddedAuthMode,
    props.authMode,
    props.defaultProfileCode,
    props.envProfile,
    props.profileStorageKey,
    props.runtime,
    props.search,
  ]);

  return useMemo(() => {
    if (props.runtime === 'embedded') {
      if (embeddedAuthMode === 'mock') {
        return {
          ...createMockFrontendAuthSession({
            profileCode: mockProfileCode!,
            runtime: props.runtime,
          }),
          login: async () => undefined,
          logout: async () => undefined,
          handleCallback: async () => null,
        };
      }

      return embeddedSession;
    }

    if (props.runtime !== 'standalone-local') {
      return createDisabledSession(props.runtime);
    }

    if (props.authMode === 'mock') {
      return {
        ...createMockFrontendAuthSession({
          profileCode: mockProfileCode!,
          runtime: props.runtime,
        }),
        login: async () => undefined,
        logout: async () => undefined,
        handleCallback: async () => null,
      };
    }

    return {
      mode: 'oidc',
      runtime: props.runtime,
      identity: oidc.user ? buildFrontendIdentityFromOidcUser(oidc.user, { clientId: props.oidcClientId }) : null,
      isAuthenticated: oidc.isAuthenticated,
      isLoading: oidc.isLoading,
      error: oidc.error,
      accessToken: oidc.getAccessToken(),
      login: oidc.login,
      logout: oidc.logout,
      handleCallback: oidc.handleCallback,
    } satisfies FrontendAuthSession;
  }, [
    mockProfileCode,
    oidc.error,
    oidc.getAccessToken,
    oidc.handleCallback,
    oidc.isAuthenticated,
    oidc.isLoading,
    oidc.login,
    oidc.logout,
    oidc.user,
    props.authMode,
    props.oidcClientId,
    props.runtime,
    embeddedSession,
    embeddedAuthMode,
  ]);
}

export function WorkspaceAuthProvider(props: WorkspaceAuthProviderProps) {
  const session = useWorkspaceAuthSession(props);

  useEffect(() => {
    if (session.mode === 'mock' || session.runtime === 'embedded') return;
    const handleAuthenticationRequired = (event: Event) => {
      const detail = (event as CustomEvent<AuthenticationRequiredMessage>).detail;
      void session.login?.(
        detail?.returnTo ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
      );
    };
    window.addEventListener(AUTHENTICATION_REQUIRED_EVENT, handleAuthenticationRequired);
    return () => window.removeEventListener(AUTHENTICATION_REQUIRED_EVENT, handleAuthenticationRequired);
  }, [session.login, session.mode, session.runtime]);

  useEffect(() => {
    registerFrontendAccessTokenAccessor(() => session.accessToken);
  }, [session.accessToken]);

  return <FrontendAuthProvider value={session}>{props.children}</FrontendAuthProvider>;
}
